/**
 * EO-01 — EventChangeDetail
 * Full Change detail: header, impact tree, threads, tasks, decisions,
 * approvals, evidence, audit timeline, lifecycle transitions.
 *
 * Route: /events/:eventId/changes/:changeId
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/services/api";
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle2, Clock,
  Zap, ChevronRight, RefreshCw, GitPullRequest, Network,
  FileText, CheckSquare, ThumbsUp, AlertCircle, Activity,
  ArrowRightCircle, ShieldAlert, Layers, Plus, Shield, Bug
} from "lucide-react";
import { toast } from "sonner";

// ── Status / severity styles ──────────────────────────────────────────────────
const SEV_STYLE = {
  CRITICAL: "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20",
  HIGH:     "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  MEDIUM:   "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  LOW:      "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
};
const ST_STYLE = {
  PROPOSED:           "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
  IMPACT_ASSESSED:    "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/20",
  AWAITING_DECISION:  "bg-[#F6EEFE] text-[#6832B8] border-[#6832B8]/20",
  AWAITING_APPROVAL:  "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  APPROVED:           "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20",
  IMPLEMENTING:       "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/20",
  IMPLEMENTED:        "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20",
  VERIFIED:           "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/30",
  CLOSED:             "bg-white text-[#706E6B] border-[#E5E5E5]",
  REJECTED:           "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20",
  CANCELLED:          "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
};
const IMP_STYLE = {
  OPEN:           "bg-[#FAFAFA] text-[#706E6B]",
  IN_PROGRESS:    "bg-[#EEF4FF] text-[#0176D3]",
  RESOLVED:       "bg-[#EAF5EE] text-[#2E844A]",
  THREAD_REQUIRED:"bg-[#FFF4E5] text-[#8C4B00]",
  WAIVED:         "bg-[#FAFAFA] text-[#706E6B]",
};
const IMP_LEVEL = {
  HIGH:     "text-[#8C4B00]",
  MEDIUM:   "text-[#8C4B00]",
  LOW:      "text-[#706E6B]",
  CRITICAL: "text-[#BA0517]",
};

// Transition action configs
const ACTION_CONFIG = {
  assess:               { label: "Run Impact Assessment", color: "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/20", needsEvidence: false },
  request_decision:     { label: "Request Decision",      color: "bg-[#F6EEFE] text-[#6832B8] border-[#6832B8]/20", needsEvidence: false },
  request_approval:     { label: "Request Approval",      color: "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30", needsEvidence: false },
  approve:              { label: "Approve Change",         color: "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20", needsEvidence: false },
  reject:               { label: "Reject",                 color: "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20", needsEvidence: false },
  start_implementation: { label: "Start Implementation",  color: "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/20", needsEvidence: false },
  mark_implemented:     { label: "Mark Implemented",      color: "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20", needsEvidence: true },
  verify:               { label: "Verify",                 color: "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20", needsEvidence: true },
  close:                { label: "Close Change",           color: "bg-[#E5E5E5] text-[#444444] border-[#C9C9C9]", needsEvidence: true },
  cancel:               { label: "Cancel",                 color: "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]", needsEvidence: false },
};

// Next actions per lifecycle status
const NEXT_ACTIONS = {
  PROPOSED:           ["assess", "cancel"],
  IMPACT_ASSESSED:    ["request_decision", "request_approval", "approve", "cancel"],
  AWAITING_DECISION:  ["request_approval", "approve", "cancel"],
  AWAITING_APPROVAL:  ["approve", "reject", "cancel"],
  APPROVED:           ["start_implementation", "cancel"],
  IMPLEMENTING:       ["mark_implemented", "cancel"],
  IMPLEMENTED:        ["verify"],
  VERIFIED:           ["close"],
};

export default function EventChangeDetail() {
  const { eventId, changeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [change, setChange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [activeSection, setActiveSection] = useState(location.state?.section || "impacts");
  const [evidenceModal, setEvidenceModal] = useState(null); // { action }
  const [evidenceRef, setEvidenceRef] = useState("");
  const [newDecision, setNewDecision] = useState(false);
  const [decisionForm, setDecisionForm] = useState({ title: "", question: "", options: "" });
  const [newApproval, setNewApproval] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ approval_type: "OPERATIONS_FEASIBILITY", title: "", description: "", requested_from_role: "OPERATIONS", required: true });
  const [newIssue, setNewIssue] = useState(false);
  const [issueForm, setIssueForm] = useState({ title: "", description: "", issue_type: "OTHER", severity: "MEDIUM", owner_role: "PROJECT_MANAGER", due_at: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${eventId}/changes/${changeId}`);
      setChange(r.data);
    } catch (err) {
      if (err?.response?.status === 404) toast.error("Change not found");
      else toast.error("Failed to load change");
    } finally {
      setLoading(false);
    }
  }, [eventId, changeId]);

  useEffect(() => { load(); }, [load]);

  async function handleAssess() {
    setAssessing(true);
    try {
      const r = await api.post(`/events/${eventId}/changes/${changeId}/assess`);
      const { impacts, thread_required, rules_evaluated, rules_matched_graph, idempotent } = r.data;
      if (idempotent) {
        toast.success(`Re-assessed: ${impacts.length} impacts (idempotent — no duplicates created)`);
      } else {
        toast.success(
          `Assessment complete: ${rules_evaluated} rules → ${rules_matched_graph} Event Graph matches → ${impacts.length} impacts`
        );
      }
      if (thread_required?.length > 0) {
        toast.warning(`${thread_required.length} service(s) have no active Thread (THREAD_REQUIRED)`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Assessment failed");
    } finally {
      setAssessing(false);
    }
  }

  async function handleTransition(action, evidence = null) {
    setTransitioning(true);
    try {
      await api.post(`/events/${eventId}/changes/${changeId}/transition`, {
        action,
        evidence_ref: evidence || undefined,
      });
      toast.success(`Change: ${action.replace(/_/g, " ")}`);
      setEvidenceModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transition failed");
    } finally {
      setTransitioning(false);
    }
  }

  async function handleCreateDecision() {
    try {
      await api.post(`/events/${eventId}/decisions`, {
        title: decisionForm.title,
        question: decisionForm.question,
        options: decisionForm.options.split(",").map(o => o.trim()).filter(Boolean),
        change_id: changeId,
      });
      toast.success("Decision created");
      setNewDecision(false);
      setDecisionForm({ title: "", question: "", options: "" });
      load();
    } catch {
      toast.error("Failed to create decision");
    }
  }

  async function handleCreateApproval() {
    try {
      await api.post(`/events/${eventId}/approvals`, { ...approvalForm, change_id: changeId });
      toast.success("Approval requested");
      setNewApproval(false);
      setApprovalForm({ approval_type: "OPERATIONS_FEASIBILITY", title: "", description: "", requested_from_role: "OPERATIONS", required: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to request approval");
    }
  }

  async function handleActionApproval(approvalId, action) {
    try {
      await api.post(`/events/${eventId}/approvals/${approvalId}/action`, { action });
      toast.success(`Approval ${action}d`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to ${action} approval`);
    }
  }

  async function handleCreateIssue() {
    try {
      await api.post(`/events/${eventId}/issues`, { ...issueForm, change_id: changeId });
      toast.success("Issue raised");
      setNewIssue(false);
      setIssueForm({ title: "", description: "", issue_type: "OTHER", severity: "MEDIUM", owner_role: "PROJECT_MANAGER", due_at: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create issue");
    }
  }

  async function handleTransitionIssue(issueId, newStatus) {
    const resolution = newStatus === "RESOLVED" || newStatus === "CLOSED"
      ? window.prompt(`Resolution note (required for ${newStatus}):`)
      : null;
    if ((newStatus === "RESOLVED" || newStatus === "CLOSED") && !resolution) return;
    try {
      await api.post(`/events/${eventId}/issues/${issueId}/transition`, { new_status: newStatus, resolution });
      toast.success(`Issue: ${newStatus}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transition failed");
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#706E6B]" />
    </div>
  );

  if (!change) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <AlertTriangle className="w-8 h-8 text-[#706E6B]" />
      <p className="text-[#706E6B] text-sm">Change not found</p>
      <button onClick={() => navigate(-1)} className="text-xs text-[#0176D3] hover:underline">Go back</button>
    </div>
  );

  const nextActions = NEXT_ACTIONS[change.status] || [];
  const isTerminal = ["CLOSED", "REJECTED", "CANCELLED"].includes(change.status);

  const prevVal = change.previous_value?.value || JSON.stringify(change.previous_value || {});
  const newVal  = change.new_value?.value || JSON.stringify(change.new_value || {});

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-5">
      {/* Back */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/imagine/events/${eventId}?tab=changes`)}
          className="p-2 hover:bg-[#FAFAFA] rounded-lg text-[#706E6B]"
          data-testid="back-to-changes-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <GitPullRequest className="w-4 h-4 text-[#0176D3]" />
            <span className="text-xs font-mono text-[#706E6B]">{change.change_number}</span>
          </div>
          <h1 className="text-lg font-bold text-[#181818] truncate">{change.title}</h1>
        </div>
        <button onClick={load} className="p-2 text-[#706E6B] hover:text-[#181818] border border-[#C9C9C9] rounded-lg">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Header Card: WHAT + STATUS */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-5 space-y-4"
        data-testid="change-header-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded font-medium border ${ST_STYLE[change.status] || ""}`}>
                {change.status?.replace(/_/g, " ")}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded font-medium border ${SEV_STYLE[change.severity] || ""}`}>
                {change.severity}
              </span>
              {change.commercial_impact_possible && (
                <span className="text-xs px-2 py-0.5 rounded font-medium bg-[#FFF4E5] text-[#8C4B00] border border-[#FE9339]/30">
                  COMMERCIAL REVIEW REQUIRED
                </span>
              )}
            </div>
            <div className="mt-2 text-xs text-[#706E6B] space-y-0.5">
              <p><span className="text-[#706E6B]">Type:</span> {change.change_type?.replace(/_/g, " ")}</p>
              <p><span className="text-[#706E6B]">Source:</span> {change.source_type?.replace(/_/g, " ")}</p>
              <p><span className="text-[#706E6B]">Requested by:</span> {change.requested_by || "—"}</p>
              {change.effective_date && (
                <p><span className="text-[#706E6B]">Effective:</span> {change.effective_date?.slice(0, 10)}</p>
              )}
            </div>
          </div>
          {/* What Changed */}
          {(prevVal || newVal) && (
            <div className="flex items-center gap-3 bg-[#FAFAFA] border border-[#C9C9C9] rounded-xl px-4 py-3 flex-shrink-0">
              <div className="text-center">
                <p className="text-[9px] text-[#706E6B] uppercase tracking-wide">Before</p>
                <p className="text-sm font-bold text-[#444444] font-mono">{prevVal || "—"}</p>
              </div>
              <ArrowRightCircle className="w-4 h-4 text-[#0176D3] flex-shrink-0" />
              <div className="text-center">
                <p className="text-[9px] text-[#706E6B] uppercase tracking-wide">After</p>
                <p className="text-sm font-bold text-[#0176D3] font-mono">{newVal || "—"}</p>
              </div>
            </div>
          )}
        </div>

        {change.reason && (
          <div className="pt-3 border-t border-[#E5E5E5]">
            <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-1">Reason</p>
            <p className="text-sm text-[#444444]">{change.reason}</p>
          </div>
        )}

        {/* Assessment provenance */}
        {change.assessed_at && (
          <div className="pt-3 border-t border-[#E5E5E5] text-[10px] text-[#706E6B] space-y-0.5 font-mono">
            <p>Assessed: {change.assessed_at?.slice(0, 16).replace("T", " ")} · by {change.assessed_by}</p>
            {change.graph_snapshot_ref && <p>Graph: {change.graph_snapshot_ref}</p>}
          </div>
        )}
      </div>

      {/* Counters */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        <CounterCard label="Impacts" value={change.impact_count} open={change.open_impact_count} color="text-[#0176D3]" />
        <CounterCard label="Decisions" value={change.decision_count} open={change.open_decision_count} color="text-[#6832B8]" />
        <CounterCard label="Tasks" value={change.task_count} open={change.open_task_count} color="text-[#8C4B00]" />
        <CounterCard label="CR Cards" value={change.cr_cards?.length} color="text-[#0176D3]" />
      </div>

      {/* Lifecycle Actions */}
      {!isTerminal && (
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4" data-testid="change-actions-bar">
          <p className="text-[10px] text-[#706E6B] uppercase tracking-wide mb-2.5">Available Actions</p>
          <div className="flex flex-wrap gap-2">
            {nextActions.map(action => {
              const cfg = ACTION_CONFIG[action] || {};
              const needsEvidence = cfg.needsEvidence;
              return (
                <button
                  key={action}
                  data-testid={`action-${action}`}
                  disabled={transitioning || assessing}
                  onClick={() => {
                    if (action === "assess") { handleAssess(); return; }
                    if (needsEvidence) { setEvidenceModal({ action }); setEvidenceRef(""); return; }
                    handleTransition(action);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${cfg.color || "bg-[#FAFAFA] text-[#444444] border-[#C9C9C9]"}`}
                >
                  {(action === "assess" && assessing) ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {cfg.label || action.replace(/_/g, " ")}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Sections nav */}
      <div className="flex gap-0.5 border-b border-[#E5E5E5] overflow-x-auto">
        {[
          { id: "impacts",   label: "Impacts",   count: change.impact_count },
          { id: "tasks",     label: "Tasks",     count: change.open_task_count },
          { id: "decisions", label: "Decisions", count: change.open_decision_count },
          { id: "approvals", label: "Approvals", count: change.pending_approval_count, icon: Shield },
          { id: "issues",    label: "Issues",    count: change.open_issue_count, icon: Bug },
          { id: "evidence",  label: "Evidence" },
          { id: "audit",     label: "Timeline" },
        ].map(s => (
          <button
            key={s.id}
            data-testid={`change-section-${s.id}`}
            onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeSection === s.id
                ? "border-[#0176D3] text-[#0176D3]"
                : "border-transparent text-[#706E6B] hover:text-[#444444]"
            }`}
          >
            {s.label}
            {s.count > 0 && (
              <span className="text-[9px] bg-[#FAFAFA] text-[#706E6B] px-1.5 py-0.5 rounded-full font-mono">
                {s.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Section content */}
      {activeSection === "impacts" && (
        <ImpactTree
          impacts={change.impacts || []}
          crCards={change.cr_cards || []}
          eventId={eventId}
        />
      )}

      {activeSection === "tasks" && (
        <TasksList tasks={change.tasks || []} />
      )}

      {activeSection === "decisions" && (
        <DecisionsList
          decisions={change.decisions || []}
          changeId={changeId}
          eventId={eventId}
          newDecision={newDecision}
          decisionForm={decisionForm}
          setDecisionForm={setDecisionForm}
          setNewDecision={setNewDecision}
          onCreateDecision={handleCreateDecision}
          onRefresh={load}
        />
      )}

      {activeSection === "approvals" && (
        <ApprovalsList
          approvals={change.approvals || []}
          changeId={changeId}
          eventId={eventId}
          newApproval={newApproval}
          approvalForm={approvalForm}
          setApprovalForm={setApprovalForm}
          setNewApproval={setNewApproval}
          onCreateApproval={handleCreateApproval}
          onActionApproval={handleActionApproval}
          onRefresh={load}
        />
      )}

      {activeSection === "issues" && (
        <IssuesList
          issues={change.issues || []}
          changeId={changeId}
          eventId={eventId}
          newIssue={newIssue}
          issueForm={issueForm}
          setIssueForm={setIssueForm}
          setNewIssue={setNewIssue}
          onCreateIssue={handleCreateIssue}
          onTransitionIssue={handleTransitionIssue}
          onRefresh={load}
        />
      )}

      {activeSection === "evidence" && (
        <EvidenceSection evidenceRefs={change.evidence_refs || []} />
      )}

      {activeSection === "audit" && (
        <AuditTimeline trail={change.audit_trail || []} />
      )}

      {/* Evidence modal */}
      {evidenceModal && (
        <EvidenceModal
          action={evidenceModal.action}
          evidenceRef={evidenceRef}
          setEvidenceRef={setEvidenceRef}
          onConfirm={() => handleTransition(evidenceModal.action, evidenceRef)}
          onCancel={() => setEvidenceModal(null)}
          loading={transitioning}
        />
      )}
    </div>
  );
}

// ── Impact Tree ────────────────────────────────────────────────────────────────
function ImpactTree({ impacts, crCards }) {
  if (impacts.length === 0) {
    return (
      <div className="text-center py-10 space-y-3" data-testid="impacts-empty">
        <Network className="w-8 h-8 text-[#444444] mx-auto" />
        <p className="text-xs text-[#706E6B]">
          No impacts generated yet. Run Impact Assessment to evaluate rules against the Event Graph.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="impacts-tree">
      <div className="flex items-center gap-2 text-[10px] text-[#706E6B] font-mono uppercase tracking-wider">
        <span>CHANGE → EVENT GRAPH → ACTIVE THREAD → IMPACT</span>
      </div>

      {impacts.map((imp, i) => {
        const hasThread = imp.affected_thread_id;
        const isThreadRequired = imp.status === "THREAD_REQUIRED";
        const linkedCard = crCards.find(c => c.metadata?.impact_id === imp.id);

        return (
          <div
            key={imp.id}
            data-testid={`impact-row-${i}`}
            className={`rounded-xl border p-4 space-y-2 ${
              isThreadRequired
                ? "border-[#FE9339]/30 bg-[#FFF4E5]"
                : "border-[#E5E5E5] bg-white"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                  isThreadRequired ? "bg-amber-400" :
                  imp.status === "RESOLVED" ? "bg-emerald-400" :
                  imp.status === "IN_PROGRESS" ? "bg-blue-400" : "bg-slate-500"
                }`} />
                <div>
                  <p className="text-sm font-medium text-[#181818]">
                    {imp.affected_entity_name}
                  </p>
                  <p className="text-xs text-[#706E6B] mt-0.5">{imp.impact_description}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${IMP_STYLE[imp.status] || ""}`}>
                  {imp.status?.replace(/_/g, " ")}
                </span>
                <span className={`text-[10px] font-medium ${IMP_LEVEL[imp.impact_level] || "text-[#706E6B]"}`}>
                  {imp.impact_level}
                </span>
              </div>
            </div>

            <div className="pl-5 text-[10px] text-[#706E6B] font-mono space-y-0.5">
              <p>Impact Type: <span className="text-[#706E6B]">{imp.impact_type}</span></p>
              {hasThread ? (
                <p className="text-emerald-500">
                  → Thread: {imp.affected_thread_name || imp.affected_thread_id}
                  {linkedCard && <span className="text-[#0176D3] ml-1">[CHANGE_REQUEST card created]</span>}
                </p>
              ) : isThreadRequired ? (
                <p className="text-[#8C4B00]">
                  → THREAD_REQUIRED: No active Thread for "{imp.affected_entity_name}". 
                  Activate Thread to resolve.
                </p>
              ) : null}
              {imp.graph_snapshot_ref && (
                <p className="text-[#444444]">Graph: {imp.graph_snapshot_ref}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tasks List ─────────────────────────────────────────────────────────────────
function TasksList({ tasks }) {
  if (tasks.length === 0) {
    return (
      <div className="text-center py-10 space-y-2" data-testid="tasks-empty">
        <CheckSquare className="w-7 h-7 text-[#444444] mx-auto" />
        <p className="text-xs text-[#706E6B]">No tasks generated yet. Run impact assessment to generate tasks.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="tasks-list">
      {tasks.map((t, i) => (
        <div key={t.id || i} className="bg-white border border-[#E5E5E5] rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-[#181818]">{t.title}</p>
              {t.description && <p className="text-xs text-[#706E6B] mt-1">{t.description}</p>}
            </div>
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
              t.status === "OPEN" ? "bg-[#FAFAFA] text-[#706E6B]" :
              t.status === "DONE" ? "bg-[#EAF5EE] text-[#2E844A]" :
              "bg-[#EEF4FF] text-[#0176D3]"
            }`}>
              {t.status}
            </span>
          </div>
          {t.metadata?.idempotency_key && (
            <p className="text-[9px] text-[#444444] font-mono mt-2">
              key: {t.metadata.idempotency_key}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Decisions List ─────────────────────────────────────────────────────────────
function DecisionsList({ decisions, changeId, eventId, newDecision, decisionForm, setDecisionForm, setNewDecision, onCreateDecision, onRefresh }) {
  const [deciding, setDeciding] = useState({});

  async function handleDecide(decId, option) {
    setDeciding(p => ({ ...p, [decId]: true }));
    try {
      await api.patch(`/events/${eventId}/decisions/${decId}`, { selected_option: option });
      toast.success("Decision recorded");
      onRefresh();
    } catch {
      toast.error("Failed to record decision");
    } finally {
      setDeciding(p => ({ ...p, [decId]: false }));
    }
  }

  return (
    <div className="space-y-3" data-testid="decisions-section">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#706E6B]">{decisions.length} decision(s) linked to this change</p>
        <button
          onClick={() => setNewDecision(!newDecision)}
          className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
          data-testid="add-decision-btn"
        >
          <Plus className="w-3 h-3" />
          Add Decision
        </button>
      </div>

      {newDecision && (
        <div className="bg-white border border-[#C9C9C9] rounded-xl p-4 space-y-3">
          <input
            placeholder="Decision title"
            value={decisionForm.title}
            onChange={e => setDecisionForm(p => ({ ...p, title: e.target.value }))}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
          />
          <input
            placeholder="Question to answer"
            value={decisionForm.question}
            onChange={e => setDecisionForm(p => ({ ...p, question: e.target.value }))}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
          />
          <input
            placeholder="Options (comma-separated)"
            value={decisionForm.options}
            onChange={e => setDecisionForm(p => ({ ...p, options: e.target.value }))}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
          />
          <div className="flex gap-2">
            <button onClick={onCreateDecision} className="px-3 py-1.5 text-xs bg-[#0176D3] text-white rounded-lg font-medium">Save</button>
            <button onClick={() => setNewDecision(false)} className="px-3 py-1.5 text-xs bg-[#FAFAFA] text-[#706E6B] rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {decisions.length === 0 && !newDecision && (
        <div className="text-center py-8">
          <p className="text-xs text-[#706E6B]">No decisions linked to this change yet.</p>
        </div>
      )}

      {decisions.map((d, i) => (
        <div key={d.id || i} className="bg-white border border-[#E5E5E5] rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-[#706E6B]">{d.decision_number}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  d.decision_status === "OPEN" ? "bg-[#F6EEFE] text-[#6832B8]" :
                  d.decision_status === "DECIDED" ? "bg-[#EAF5EE] text-[#2E844A]" :
                  "bg-[#FAFAFA] text-[#706E6B]"
                }`}>
                  {d.decision_status}
                </span>
              </div>
              <p className="text-sm font-medium text-[#181818] mt-1">{d.title}</p>
              {d.question && <p className="text-xs text-[#706E6B] mt-0.5">{d.question}</p>}
              {d.impact_id && (
                <p className="text-[10px] text-[#706E6B] mt-1 font-mono">Linked impact: {d.impact_id}</p>
              )}
            </div>
          </div>
          {d.options?.length > 0 && d.decision_status === "OPEN" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {d.options.map((o, oi) => (
                <button
                  key={oi}
                  onClick={() => handleDecide(d.id, o)}
                  disabled={deciding[d.id]}
                  className="px-3 py-1.5 text-xs bg-[#FAFAFA] text-[#444444] border border-[#C9C9C9] rounded-lg hover:border-emerald-700 hover:text-[#2E844A] transition-colors"
                >
                  {o}
                </button>
              ))}
            </div>
          )}
          {d.selected_option && (
            <div className="mt-2 text-xs text-[#2E844A]">
              Selected: {d.selected_option} {d.decided_at && `· ${d.decided_at?.slice(0, 10)}`}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Evidence Section ───────────────────────────────────────────────────────────
function EvidenceSection({ evidenceRefs }) {
  if (evidenceRefs.length === 0) {
    return (
      <div className="text-center py-10 space-y-2" data-testid="evidence-empty">
        <FileText className="w-7 h-7 text-[#444444] mx-auto" />
        <p className="text-xs text-[#706E6B]">
          No evidence attached. Evidence is required before marking Implemented, Verified, or Closed.
        </p>
        <p className="text-[10px] text-[#706E6B] font-mono">
          Evidence proves that the authoritative domain change was actually completed.
          Completing generated tasks alone is insufficient.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="evidence-list">
      {evidenceRefs.map((ev, i) => (
        <div key={i} className="bg-white border border-[#E5E5E5] rounded-xl p-3 flex items-start gap-3">
          <FileText className="w-4 h-4 text-[#706E6B] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-[#181818]">{ev.ref}</p>
            <p className="text-[10px] text-[#706E6B] mt-0.5">
              {ev.action?.replace(/_/g, " ")} · {ev.at?.slice(0, 16).replace("T", " ")} · by {ev.actor}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Audit Timeline ─────────────────────────────────────────────────────────────
function AuditTimeline({ trail }) {
  if (trail.length === 0) {
    return <p className="text-xs text-[#706E6B] py-8 text-center">No audit events yet.</p>;
  }
  return (
    <div className="space-y-2 relative" data-testid="audit-timeline">
      <div className="absolute left-3 top-0 bottom-0 w-px bg-[#FAFAFA]" />
      {trail.map((a, i) => (
        <div key={i} className="pl-8 relative">
          <div className="absolute left-1.5 top-1.5 w-3 h-3 rounded-full bg-[#FAFAFA] border border-[#C9C9C9]" />
          <div className="bg-white border border-[#E5E5E5] rounded-xl px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium text-[#444444]">{a.action?.replace(/_/g, " ")}</p>
              <p className="text-[10px] text-[#706E6B] flex-shrink-0">
                {a.timestamp?.slice(0, 16).replace("T", " ")}
              </p>
            </div>
            {a.summary && <p className="text-[10px] text-[#706E6B] mt-0.5">{a.summary}</p>}
            {a.actor_email && <p className="text-[10px] text-[#444444] mt-0.5 font-mono">{a.actor_email}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Evidence Modal ─────────────────────────────────────────────────────────────
function EvidenceModal({ action, evidenceRef, setEvidenceRef, onConfirm, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-[#C9C9C9] rounded-2xl w-full max-w-md p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-[#181818]">Evidence Required</h3>
          <p className="text-xs text-[#706E6B] mt-1">
            Action "{action?.replace(/_/g, " ")}" requires evidence that the authoritative domain
            change was actually completed. Completing tasks alone is not sufficient.
          </p>
        </div>
        <textarea
          value={evidenceRef}
          onChange={e => setEvidenceRef(e.target.value)}
          rows={3}
          placeholder="Describe the evidence (e.g. vendor confirmation email, system record ID, approval reference...)"
          className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none resize-none focus:border-[#0176D3]"
          data-testid="evidence-ref-input"
        />
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2 text-xs bg-[#FAFAFA] text-[#706E6B] rounded-lg hover:bg-[#E5E5E5]">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!evidenceRef.trim() || loading}
            className="flex-1 py-2 text-xs bg-[#0176D3] text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="evidence-confirm-btn"
          >
            {loading && <Loader2 className="w-3 h-3 animate-spin" />}
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Counter Card ───────────────────────────────────────────────────────────────
function CounterCard({ label, value, open, color }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-xl p-3">
      <p className={`text-xs uppercase tracking-wide mb-1 ${color}`}>{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value ?? 0}</p>
      {open !== undefined && open > 0 && (
        <p className="text-[10px] text-[#706E6B] mt-0.5">{open} open</p>
      )}
    </div>
  );
}


// ── Approval Status styles ─────────────────────────────────────────────────────
const APPR_ST_STYLE = {
  REQUESTED: "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  APPROVED:  "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20",
  REJECTED:  "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20",
  CANCELLED: "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
  EXPIRED:   "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
};
const ISSUE_SEV_STYLE = {
  CRITICAL: "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20",
  HIGH:     "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  MEDIUM:   "bg-[#FFF4E5] text-[#8C4B00] border-[#FE9339]/30",
  LOW:      "bg-[#FAFAFA] text-[#706E6B] border-[#C9C9C9]",
};
const ISSUE_ST_STYLE = {
  OPEN:          "bg-[#FAFAFA] text-[#706E6B]",
  INVESTIGATING: "bg-[#EEF4FF] text-[#0176D3]",
  BLOCKED:       "bg-[#FCEBEC] text-[#BA0517]",
  RESOLVED:      "bg-[#EAF5EE] text-[#2E844A]",
  CLOSED:        "bg-[#FAFAFA] text-[#706E6B]",
};

const ROLE_LABELS = {
  RELATIONSHIP_MANAGER: "Relationship Manager",
  PROJECT_MANAGER:      "Project Manager",
  EVENT_PLANNER_DESIGNER: "Event Planner / Designer",
  CUSTOMER:   "Customer",
  OPERATIONS: "Operations",
  FINANCE:    "Finance",
  LEGAL:      "Legal",
  VENDOR:     "Vendor",
  OTHER:      "Other",
};
const roleLabel = (r) => ROLE_LABELS[r] || r;

// ── Approvals List ─────────────────────────────────────────────────────────────
function ApprovalsList({ approvals, changeId, eventId, newApproval, approvalForm, setApprovalForm, setNewApproval, onCreateApproval, onActionApproval, onRefresh }) {
  const APPROVAL_TYPES = ["CUSTOMER_SCOPE", "OPERATIONS_FEASIBILITY", "FINANCE_COMMERCIAL", "VENDOR_CONFIRMATION", "LEGAL_COMPLIANCE", "OTHER"];
  const ROLES = ["CUSTOMER", "OPERATIONS", "FINANCE", "LEGAL", "VENDOR", "RELATIONSHIP_MANAGER"];

  return (
    <div className="space-y-3" data-testid="approvals-section">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#706E6B]">{approvals.length} approval(s) for this change</p>
        <button
          onClick={() => setNewApproval(!newApproval)}
          className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
          data-testid="request-approval-btn"
        >
          <Plus className="w-3 h-3" />
          Request Approval
        </button>
      </div>

      {newApproval && (
        <div className="bg-white border border-[#C9C9C9] rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <select
              value={approvalForm.approval_type}
              onChange={e => setApprovalForm(p => ({ ...p, approval_type: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none col-span-2"
            >
              {APPROVAL_TYPES.map(t => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
            <select
              value={approvalForm.requested_from_role}
              onChange={e => setApprovalForm(p => ({ ...p, requested_from_role: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
            >
              {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-[#444444] px-2">
              <input type="checkbox" checked={approvalForm.required} onChange={e => setApprovalForm(p => ({ ...p, required: e.target.checked }))} />
              Required (blocks Change approval)
            </label>
          </div>
          <input
            placeholder="Title (optional — auto-generated if blank)"
            value={approvalForm.title}
            onChange={e => setApprovalForm(p => ({ ...p, title: e.target.value }))}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
          />
          <textarea
            placeholder="Description / context"
            value={approvalForm.description}
            onChange={e => setApprovalForm(p => ({ ...p, description: e.target.value }))}
            rows={2}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none resize-none"
          />
          <div className="flex gap-2">
            <button onClick={onCreateApproval} className="px-3 py-1.5 text-xs bg-[#0176D3] text-white rounded-lg font-medium" data-testid="save-approval-btn">Request</button>
            <button onClick={() => setNewApproval(false)} className="px-3 py-1.5 text-xs bg-[#FAFAFA] text-[#706E6B] rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {approvals.length === 0 && !newApproval && (
        <div className="text-center py-10 space-y-2" data-testid="approvals-empty">
          <Shield className="w-7 h-7 text-[#C9C9C9] mx-auto" />
          <p className="text-xs text-[#706E6B]">No approvals requested yet.</p>
          <p className="text-[10px] text-[#444444]">Approvals are auto-created when ACTIVE impact rules have approval_required=true. You can also request manually above.</p>
        </div>
      )}

      {approvals.map((a, i) => (
        <div key={a.id || i} className="bg-white border border-[#E5E5E5] rounded-xl p-4 space-y-2" data-testid={`approval-${a.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-[#706E6B]">{a.approval_number}</span>
                {a.required && <span className="text-[9px] bg-[#FCEBEC] text-[#BA0517] px-1 py-0.5 rounded">REQUIRED</span>}
              </div>
              <p className="text-sm font-medium text-[#181818]">{a.title}</p>
              {a.description && <p className="text-xs text-[#706E6B] mt-1">{a.description}</p>}
              <p className="text-[10px] text-[#706E6B] mt-1">
                From: <span className="font-medium text-[#444444]">{roleLabel(a.requested_from_role)}</span>
                {" · "}Type: <span className="font-medium text-[#444444]">{a.approval_type?.replace(/_/g, " ")}</span>
              </p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded border font-medium flex-shrink-0 ${APPR_ST_STYLE[a.status] || ""}`}>
              {a.status}
            </span>
          </div>
          {a.status === "REQUESTED" && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onActionApproval(a.id, "approve")}
                className="px-3 py-1 text-[10px] bg-[#EAF5EE] text-[#2E844A] border border-[#2E844A]/20 rounded-lg font-medium hover:opacity-90"
                data-testid={`approve-approval-${a.id}`}
              >
                Approve
              </button>
              <button
                onClick={() => onActionApproval(a.id, "reject")}
                className="px-3 py-1 text-[10px] bg-[#FCEBEC] text-[#BA0517] border border-[#BA0517]/20 rounded-lg font-medium hover:opacity-90"
                data-testid={`reject-approval-${a.id}`}
              >
                Reject
              </button>
            </div>
          )}
          {a.decided_at && (
            <p className="text-[9px] text-[#706E6B] font-mono">
              Decided: {new Date(a.decided_at).toLocaleString()}
              {a.decision_note && <span className="ml-2 text-[#444444]">{a.decision_note}</span>}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Issues List ────────────────────────────────────────────────────────────────
function IssuesList({ issues, changeId, eventId, newIssue, issueForm, setIssueForm, setNewIssue, onCreateIssue, onTransitionIssue, onRefresh }) {
  const ISSUE_TYPES = ["CAPACITY_CONFLICT", "VENDOR_CONSTRAINT", "TIMELINE_CONFLICT", "BUDGET_OVERRUN", "DESIGN_MISMATCH", "OPERATIONAL_GAP", "EXTERNAL_DEPENDENCY", "CUSTOMER_ESCALATION", "OTHER"];
  const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
  const ROLES = ["PROJECT_MANAGER", "OPERATIONS", "VENDOR", "FINANCE", "CUSTOMER", "OTHER"];

  return (
    <div className="space-y-3" data-testid="issues-section">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#706E6B]">{issues.length} issue(s) linked to this change</p>
        <button
          onClick={() => setNewIssue(!newIssue)}
          className="flex items-center gap-1 text-xs text-[#0176D3] hover:underline"
          data-testid="raise-issue-btn"
        >
          <Plus className="w-3 h-3" />
          Raise Issue
        </button>
      </div>

      {newIssue && (
        <div className="bg-white border border-[#C9C9C9] rounded-xl p-4 space-y-3">
          <input
            placeholder="Issue title *"
            value={issueForm.title}
            onChange={e => setIssueForm(p => ({ ...p, title: e.target.value }))}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
          />
          <textarea
            placeholder="Description / what happened"
            value={issueForm.description}
            onChange={e => setIssueForm(p => ({ ...p, description: e.target.value }))}
            rows={2}
            className="w-full bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none resize-none"
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={issueForm.severity}
              onChange={e => setIssueForm(p => ({ ...p, severity: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
            >
              {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={issueForm.issue_type}
              onChange={e => setIssueForm(p => ({ ...p, issue_type: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
            >
              {ISSUE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
            <select
              value={issueForm.owner_role}
              onChange={e => setIssueForm(p => ({ ...p, owner_role: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
            >
              {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
            <input
              type="date"
              placeholder="Due date (optional)"
              value={issueForm.due_at || ""}
              onChange={e => setIssueForm(p => ({ ...p, due_at: e.target.value }))}
              className="bg-[#FAFAFA] border border-[#C9C9C9] rounded-lg text-sm text-[#181818] px-3 py-2 outline-none"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={onCreateIssue} className="px-3 py-1.5 text-xs bg-[#0176D3] text-white rounded-lg font-medium" data-testid="save-issue-btn">Raise Issue</button>
            <button onClick={() => setNewIssue(false)} className="px-3 py-1.5 text-xs bg-[#FAFAFA] text-[#706E6B] rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {issues.length === 0 && !newIssue && (
        <div className="text-center py-10 space-y-2" data-testid="issues-empty">
          <Bug className="w-7 h-7 text-[#C9C9C9] mx-auto" />
          <p className="text-xs text-[#706E6B]">No issues raised yet.</p>
          <p className="text-[10px] text-[#444444]">Event Issues track operational problems (vendor conflicts, capacity violations) — distinct from software UAT Feedback.</p>
        </div>
      )}

      {issues.map((iss, i) => {
        const transitions = {
          OPEN:          ["INVESTIGATING", "RESOLVED"],
          INVESTIGATING: ["BLOCKED", "RESOLVED"],
          BLOCKED:       ["INVESTIGATING", "RESOLVED"],
          RESOLVED:      ["CLOSED"],
          CLOSED:        [],
        }[iss.status] || [];
        return (
          <div key={iss.id || i} className="bg-white border border-[#E5E5E5] rounded-xl p-4 space-y-2" data-testid={`issue-${iss.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono text-[#706E6B]">{iss.issue_number}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border font-medium ${ISSUE_SEV_STYLE[iss.severity] || ""}`}>
                    {iss.severity}
                  </span>
                </div>
                <p className="text-sm font-medium text-[#181818]">{iss.title}</p>
                {iss.description && <p className="text-xs text-[#706E6B] mt-1">{iss.description}</p>}
                <p className="text-[10px] text-[#706E6B] mt-1">
                  Owner: <span className="font-medium text-[#444444]">{roleLabel(iss.owner_role)}</span>
                  {" · "}Type: <span className="font-medium text-[#444444]">{iss.issue_type?.replace(/_/g, " ")}</span>
                  {iss.due_at && <span className="ml-2">Due: {new Date(iss.due_at).toLocaleDateString()}</span>}
                </p>
                {iss.resolution && (
                  <p className="text-[10px] text-[#2E844A] mt-1">Resolution: {iss.resolution}</p>
                )}
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded font-medium flex-shrink-0 ${ISSUE_ST_STYLE[iss.status] || ""}`}>
                {iss.status}
              </span>
            </div>
            {transitions.length > 0 && (
              <div className="flex gap-2 pt-1">
                {transitions.map(t => (
                  <button
                    key={t}
                    onClick={() => onTransitionIssue(iss.id, t)}
                    className={`px-2.5 py-1 text-[10px] rounded-lg font-medium border ${
                      t === "RESOLVED" || t === "CLOSED" ? "bg-[#EAF5EE] text-[#2E844A] border-[#2E844A]/20" :
                      t === "BLOCKED" ? "bg-[#FCEBEC] text-[#BA0517] border-[#BA0517]/20" :
                      "bg-[#EEF4FF] text-[#0176D3] border-[#0176D3]/20"
                    }`}
                    data-testid={`issue-${iss.id}-${t.toLowerCase()}`}
                  >
                    → {t.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
